// ADR 0038: every agent of the app in one manifest shape (the host's platform personas and the app's own agents,
// each with its source), where the app uses them (computed from its sources by the host), and the views over both:
// the source badge, the per-agent tabs (a registered list, ./tabs.tsx), the "Used in" list and the usage page
// with each app's agents.
import {
  AlertTriangle,
  AppWindow,
  Bot,
  FileCode2,
  FlaskConical,
  GitCommit,
  Loader2,
  MessageSquare,
  Package,
  Workflow,
} from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router';
import type { AgentCatalog, AgentUsage, CatalogAgent } from '@cortex/sdk-agents-client';
import { useAgentsHost, useAgentsKey, useAgentsQuery } from './host.tsx';
import { ago } from './model.ts';
import { unwrap } from './ui.tsx';

export type { AgentCatalog, AgentUsage, CatalogAgent };
export type UsageSite = AgentUsage['sites'][number];

const C = '/agent-catalog' as const;
const U = '/agent-usage' as const;

/** The app's agent catalog (platform personas, then project agents), cached under the agents key. */
export function useAgentCatalog() {
  const { client } = useAgentsHost();
  const k = useAgentsKey();
  return useAgentsQuery(k('catalog'), () => unwrap(client.fetch.GET(C)));
}

/** The app's usage index. */
export function useAgentUsage() {
  const { client } = useAgentsHost();
  const k = useAgentsKey();
  return useAgentsQuery(k('usage'), () => unwrap(client.fetch.GET(U)));
}

/**
 * "Platform · sdk-chat-assistant 1.5.0", "Project override of sdk-chat-assistant 1.5.0 · commit 1a2b3c4" (a persona
 * this project overrides, ADR 0038 §2) or "Project · commit 1a2b3c4".
 */
export function sourceText(s: CatalogAgent['source'], override?: CatalogAgent['override']): string {
  if (s.kind === 'platform') {
    const pkg = `${s.package.replace(/^@[^/]+\//, '')} ${s.version}`;
    if (override)
      return `Project override of ${pkg}${override.commit ? ` · commit ${override.commit.slice(0, 7)}` : ''}`;
    return `Platform · ${pkg}`;
  }
  return s.commit ? `Project · commit ${s.commit.slice(0, 7)}` : 'Project';
}

export function SourceBadge({
  source,
  override,
  testId,
}: {
  source: CatalogAgent['source'];
  override?: CatalogAgent['override'];
  testId?: string;
}) {
  const Icon = source.kind === 'platform' && !override ? Package : GitCommit;
  return (
    <span
      data-testid={testId}
      data-source={source.kind}
      data-override={override ? 'true' : undefined}
      title={
        source.kind === 'platform'
          ? `${source.package}@${source.version}: ${source.path}${override ? `\n${override.path}` : ''}`
          : source.paths.join('\n')
      }
      className="inline-flex items-center gap-1 rounded-full border border-card-border px-1.5 py-0.5 text-[10px] text-page-muted whitespace-nowrap"
    >
      <Icon size={10} /> {sourceText(source, override)}
    </span>
  );
}

const KIND: Record<UsageSite['kind'], { label: string; icon: typeof Bot }> = {
  ask: { label: 'ctx.agents.ask', icon: Bot },
  llm: { label: 'raw ctx.llm', icon: AlertTriangle },
  chat: { label: 'chat', icon: MessageSquare },
  workflow: { label: 'workflow step', icon: Workflow },
  test: { label: 'test', icon: FlaskConical },
};

/** One site: its kind, its place in a file, app and what is there; a raw call naming no declared agent is flagged. */
export function UsageRow({ site }: { site: UsageSite }) {
  const k = KIND[site.kind];
  return (
    <li
      className="py-1.5 text-xs flex items-start gap-2"
      data-testid="usage-site"
      data-kind={site.kind}
      data-undeclared={site.undeclared ? 'true' : 'false'}
    >
      <k.icon
        size={12}
        className={'mt-0.5 shrink-0 ' + (site.undeclared ? 'text-amber-600' : 'text-page-muted')}
      />
      <span className="min-w-0">
        <span className="font-mono text-page-text break-all">
          {site.file}:{site.line}
        </span>
        <span className="text-page-muted"> · {k.label}</span>
        {site.app ? <span className="text-page-muted"> · app {site.app}</span> : null}
        {site.undeclared ? (
          <span
            className="ml-1 rounded bg-amber-100 dark:bg-amber-950/40 px-1 text-amber-800 dark:text-amber-300"
            data-testid="usage-undeclared"
          >
            undeclared
          </span>
        ) : null}
        <span className="block text-page-muted">{site.detail}</span>
      </span>
    </li>
  );
}

/** The sections of an agent's Used in, by what uses it, in this order (an empty one shows as such). */
const SECTIONS: { id: Section; title: string; kinds: UsageSite['kind'][]; none: string }[] = [
  { id: 'code', title: 'Apps', kinds: ['ask', 'llm'], none: 'apps' },
  { id: 'workflow', title: 'Workflow steps', kinds: ['workflow'], none: 'workflows' },
  { id: 'chat', title: 'Chat', kinds: ['chat'], none: 'chat' },
  { id: 'test', title: 'Tests', kinds: ['test'], none: 'tests' },
];
type Section = 'code' | 'workflow' | 'chat' | 'test';

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** One row of Used in: what it is, where it opens, the sites under it. */
interface UsedRow {
  key: string;
  kind: 'App' | 'Workflow' | 'Suite';
  name: string;
  /** The line under the name (a file link, a step, a test count). */
  what: React.ReactNode;
  /** Where the row opens, and the words of its link. */
  to: string | null;
  go: string;
  sites: UsageSite[];
}

/** What a call in code does with the agent, in words. */
const callText = (s: UsageSite) =>
  s.undeclared
    ? s.detail
    : s.kind === 'ask'
      ? 'the code asks this agent'
      : 'the model is called as this agent';

const fileName = (file: string) => file.split('/').pop() ?? file;

/** The rows of each section: a call per row, a workflow step once (its declaration and versions), a suite once. */
function usedRows(
  section: Section,
  sites: UsageSite[],
  link: { paths: ReturnType<typeof useAgentsHost>['paths']; self: string },
): UsedRow[] {
  const { paths } = link;
  const group = (keyOf: (s: UsageSite) => string) => {
    const by = new Map<string, UsageSite[]>();
    for (const s of sites) by.set(keyOf(s), [...(by.get(keyOf(s)) ?? []), s]);
    return [...by];
  };
  if (section === 'code')
    return sites.map((s) => ({
      key: `${s.file}:${s.line}`,
      kind: 'App',
      name: s.app ?? s.file,
      what: (
        <>
          <span className="font-mono text-nav-active" title={`${s.file}:${s.line}`}>
            {fileName(s.file)}:{s.line}
          </span>
          <span className="text-page-muted"> · {callText(s)}</span>
        </>
      ),
      to: paths.code ? paths.code(s.file, s.line) : null,
      go: 'Open code',
      sites: [s],
    }));
  if (section === 'workflow')
    return group((s) => `${s.workflow ?? s.detail}\u0000${s.stepId ?? s.step ?? `${s.file}:${s.line}`}`).map(
      ([key, list]) => {
        const s = list.find((x) => x.workflowTitle) ?? list[0]!;
        const step = list.find((x) => x.step)?.step;
        const stepId = list.find((x) => x.stepId)?.stepId;
        return {
          key,
          kind: 'Workflow',
          name: s.workflowTitle ?? s.workflow ?? s.detail,
          what: step ? (
            <>
              step <span className="font-medium text-page-text">“{step}”</span>
            </>
          ) : (
            <span className="text-page-muted">a step done by this agent</span>
          ),
          to: paths.workflow && s.workflow ? paths.workflow(s.workflow, stepId) : null,
          go: 'Open workflow',
          sites: list,
        };
      },
    );
  if (section === 'chat')
    return group((s) => s.app ?? s.file).map(([key, list]) => ({
      key,
      kind: 'App',
      name: list[0]!.app ?? key,
      what: <span className="text-page-muted">{list[0]!.detail}</span>,
      to: paths.app && list[0]!.app ? paths.app(list[0]!.app) : null,
      go: 'Open app',
      sites: list,
    }));
  return group((s) => s.suite ?? '').map(([suite, list]) => ({
    key: `suite:${suite}`,
    kind: 'Suite',
    name: suite || 'tests',
    what: <span className="text-page-muted">{plural(list.length, 'test case')}</span>,
    to: paths.agent(link.self, `tab=evaluations${suite ? `&suite=${encodeURIComponent(suite)}` : ''}`),
    go: 'Open in Evaluations',
    sites: list,
  }));
}

/** The sites of one subject (`agent:<key>` / `persona:<key>`) by what uses it: code, workflow steps, chat, tests. */
export function UsedIn({ subject }: { subject: string }) {
  const { paths } = useAgentsHost();
  const usage = useAgentUsage();
  if (usage.loading && !usage.data)
    return (
      <div className="flex items-center gap-2 text-sm text-page-muted">
        <Loader2 size={14} className="animate-spin" /> Reading the app’s code…
      </div>
    );
  if (usage.error) return <div className="text-sm text-red-600">{usage.error}</div>;
  const u = usage.data!;
  const sites = u.sites.filter((s) => s.subject === subject);
  const self = subject.replace(/^(agent|persona):/, '');
  // An app's chat is always a platform persona (the orchestrator or the Data analyst, ADR 0038 §4): a project
  // agent is never one, so its page has no Chat section.
  const sections = subject.startsWith('agent:') ? SECTIONS.filter((x) => x.id !== 'chat') : SECTIONS;
  const parts = sections.map((x) => {
    const rows = usedRows(
      x.id,
      sites.filter((s) => x.kinds.includes(s.kind)),
      { paths, self },
    );
    return { ...x, rows };
  });
  const used = parts.filter((x) => x.rows.length);
  const unused = parts.filter((x) => !x.rows.length);
  const apps = new Set(parts.find((x) => x.id === 'code')!.rows.map((r) => r.name)).size;
  const phrase: Record<Section, (n: number) => string> = {
    code: () => plural(apps, 'app'),
    workflow: () =>
      plural(new Set(parts.find((x) => x.id === 'workflow')!.rows.map((r) => r.name)).size, 'workflow'),
    chat: (n) => plural(n, 'chat'),
    test: (n) => plural(n, 'test suite'),
  };
  const said = used.map((x) => phrase[x.id](x.rows.length));
  return (
    <div data-testid="agent-used-in">
      <div className="text-sm text-page-muted mb-3" data-testid="agent-used-in-summary">
        {used.length ? (
          <>
            Used in{' '}
            <span className="text-page-text font-medium">
              {said.length > 1 ? `${said.slice(0, -1).join(', ')} and ${said.at(-1)}` : said[0]}
            </span>
            .{unused.length ? ` Not used in ${unused.map((x) => x.none).join(' or ')}.` : ''}
          </>
        ) : (
          <span data-testid="agent-used-in-empty">Nothing in this app names it.</span>
        )}
        <span className="block text-xs">
          Read from the app’s code
          {u.commit ? ` (${u.commit.slice(0, 7)})` : ''}.
        </span>
      </div>
      {used.length
        ? parts.map((x) => (
            <section key={x.id} className="mb-4" data-testid={`agent-used-in-${x.id}`}>
              <h3
                className={
                  'text-sm m-0 mb-1.5 flex items-center gap-1.5 ' +
                  (x.rows.length ? 'font-semibold text-page-title' : 'font-normal text-page-muted')
                }
              >
                {x.title} <span className="font-normal text-page-muted">{x.rows.length}</span>
              </h3>
              {x.rows.length ? (
                <ul className="m-0 p-0 list-none rounded-md border border-card-border divide-y divide-divider">
                  {x.rows.map((r) => (
                    <UsedRowItem key={r.key} row={r} section={x.id} />
                  ))}
                </ul>
              ) : null}
            </section>
          ))
        : null}
      {u.notes.map((n) => (
        <div key={n} className="text-xs text-page-muted mt-2">
          {n}
        </div>
      ))}
    </div>
  );
}

function UsedRowItem({ row, section }: { row: UsedRow; section: Section }) {
  const undeclared = row.sites.some((s) => s.undeclared);
  const body = (
    <>
      {row.to ? (
        <span className="float-right text-xs text-page-muted group-hover:text-nav-active">{row.go} ›</span>
      ) : null}
      <span className="inline-block text-[11px] text-page-muted border border-card-border rounded px-1.5 mr-1.5">
        {row.kind}
      </span>{' '}
      <span className="font-medium text-page-text">{row.name}</span>
      {undeclared ? (
        <span
          className="ml-1 rounded bg-amber-100 dark:bg-amber-950/40 px-1 text-xs text-amber-800 dark:text-amber-300"
          data-testid="usage-undeclared"
        >
          undeclared
        </span>
      ) : null}
      <span className="block text-xs mt-0.5">{row.what}</span>
    </>
  );
  return (
    <li
      data-testid="usage-site"
      data-kind={section === 'code' ? row.sites[0]!.kind : section}
      data-undeclared={undeclared ? 'true' : 'false'}
      data-file={row.sites.map((s) => `${s.file}:${s.line}`).join(' ')}
      className="text-sm"
    >
      {row.to ? (
        <Link to={row.to} className="group block px-3 py-2 no-underline text-inherit hover:bg-card-border/30">
          {body}
        </Link>
      ) : (
        <div className="px-3 py-2">{body}</div>
      )}
    </li>
  );
}

/**
 * What a platform agent may change, from its tools: `Reads data only`, or `Changes Board cards` (`… and 4 more`
 * past three, the whole list in `all`). Without the tools' access (an older host) it falls back to the agent's own.
 */
export function changesText(
  a: Pick<CatalogAgent, 'access' | 'tools'>,
  /** What each of the host's tool groups changes, in plain words (`AgentsHost.toolChanges`). */
  changes: Record<string, string> = {},
): { text: string; all: string } {
  if (!a.tools.some((t) => t.access)) {
    const text = a.access === 'read' ? 'Reads data only' : 'Can change data';
    return { text, all: text };
  }
  const things = [
    ...new Set(
      a.tools.filter((t) => t.access === 'write').map((t) => changes[t.group ?? ''] ?? t.group ?? t.name),
    ),
  ];
  if (!things.length) return { text: 'Reads data only', all: 'Reads data only' };
  const list = (xs: string[]) => (xs.length > 1 ? `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}` : xs[0]!);
  return {
    text: `Changes ${things.length > 3 ? `${things.slice(0, 3).join(', ')} and ${things.length - 3} more` : list(things)}`,
    all: `Changes ${list(things)}`,
  };
}

/** The run counts of one subject from the ledger (last run, how many). */
export function RunStats({ subject }: { subject: string }) {
  const usage = useAgentUsage();
  const r = usage.data?.runs.find((x) => x.subject === subject);
  return (
    <div className="text-xs text-page-muted" data-testid="agent-run-stats">
      {r
        ? `${r.runs} run${r.runs === 1 ? '' : 's'} in the ledger · last ${ago(r.lastAt)}`
        : 'No runs in the ledger yet.'}
    </div>
  );
}

const subjectTitle = (catalog: AgentCatalog | null, s: string) =>
  catalog?.agents.find((a) => a.subject === s)?.title ?? s.replace(/^(agent|persona):/, '');

/**
 * The usage page (`?view=usage`): every site by file and line (the raw `ctx.llm` calls flagged), and
 * each app with the agents it uses (`?app=<key>` shows one app's).
 */
export function UsagePanel({
  app,
  openAgent,
  openApp,
}: {
  app: string | null;
  openAgent(subject: string): void;
  openApp(app: string | null): void;
}) {
  const usage = useAgentUsage();
  const catalog = useAgentCatalog();
  if (usage.loading && !usage.data)
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-page-muted">
        <Loader2 size={14} className="animate-spin" /> Reading the app’s code…
      </div>
    );
  if (usage.error) return <div className="p-6 text-sm text-red-600">{usage.error}</div>;
  const u = usage.data!;
  const apps = app ? u.apps.filter((a) => a.app === app) : u.apps;
  const sites = app ? u.sites.filter((s) => s.app === app) : u.sites;
  const undeclared = sites.filter((s) => s.undeclared).length;
  return (
    <div className="p-6 max-w-5xl" data-testid="agents-usage-panel">
      <h1 className="text-xl font-semibold text-page-title m-0 flex items-center gap-2">
        <FileCode2 size={18} /> {app ? `Agents used by app ${app}` : 'Where agents are used'}
      </h1>
      <p className="text-sm text-page-muted mt-1">
        Computed from the app’s code
        {u.commit ? ` at ${u.commit.slice(0, 7)}` : ''}, never maintained by hand. {sites.length} site
        {sites.length === 1 ? '' : 's'}
        {undeclared ? `, ${undeclared} undeclared` : ''}.
        {app ? (
          <button
            type="button"
            className="ml-2 text-nav-active bg-transparent border-0 cursor-pointer p-0"
            onClick={() => openApp(null)}
          >
            All apps
          </button>
        ) : null}
      </p>
      <h2 className="text-sm font-semibold text-page-title mt-5 mb-2">Apps and the agents they use</h2>
      {apps.length ? (
        <ul className="m-0 p-0 list-none grid gap-2" data-testid="usage-apps">
          {apps.map((a) => (
            <li
              key={a.app}
              className="rounded-md border border-card-border bg-card-bg p-3"
              data-testid={`usage-app-${a.app}`}
            >
              <button
                type="button"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-page-text bg-transparent border-0 cursor-pointer p-0"
                onClick={() => openApp(a.app)}
              >
                <AppWindow size={14} /> {a.app}
              </button>
              <div className="flex flex-wrap gap-1 mt-2">
                {a.subjects.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`usage-app-agent-${s}`}
                    onClick={() => openAgent(s)}
                    className="inline-flex items-center gap-1 rounded-full border border-card-border px-2 py-0.5 text-xs text-page-text bg-transparent cursor-pointer hover:bg-card-border/30"
                  >
                    <Bot size={11} /> {subjectTitle(catalog.data, s)}
                  </button>
                ))}
                {a.undeclared ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-950/40 px-2 py-0.5 text-xs text-amber-800 dark:text-amber-300">
                    <AlertTriangle size={11} /> {a.undeclared} undeclared model call
                    {a.undeclared === 1 ? '' : 's'}
                  </span>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-sm text-page-muted">No app here uses an agent or a model.</div>
      )}
      <h2 className="text-sm font-semibold text-page-title mt-5 mb-2">Every site</h2>
      <ul className="m-0 p-0 list-none divide-y divide-divider" data-testid="usage-sites">
        {sites.map((s) => (
          <UsageRow key={`${s.file}:${s.line}:${s.kind}`} site={s} />
        ))}
      </ul>
      {u.notes.map((n) => (
        <div key={n} className="text-xs text-page-muted mt-2">
          {n}
        </div>
      ))}
    </div>
  );
}
